import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import qrcode from 'qrcode-generator';
import { AppDispatch, RootState } from '../store/store';
import { setup2FA, confirm2FA, disable2FA, clearSetupData } from '../store/slices/twoFactorSlice';
import { updateContactData } from '../store/slices/profileSlice';
import { addNotification } from '../store/slices/notificationSlice';
import Input from './ui/Input';
import Modal from './ui/Modal';

interface TwoFactorTabProps {
  // Switches the parent ProfilePage tab when the user needs to delete the
  // DataSource before disabling 2FA (HTTP 409 / two_factor_locked_by_data_source).
  onSwitchToDataSourceTab?: () => void;
}

const TwoFactorTab: React.FC<TwoFactorTabProps> = ({ onSwitchToDataSourceTab }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector((state: RootState) => state.profile);
  const { setupData, setupLoading, confirmLoading, disableLoading } = useSelector((state: RootState) => state.twoFactor);
  const isTwoFactorEnabled = profile.contact?.isTwoFactorEnabled || false;

  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [error, setError] = useState('');
  const [isDataSourceLockModalOpen, setIsDataSourceLockModalOpen] = useState(false);

  useEffect(() => {
    if (!setupData?.provisioningUri || !qrCanvasRef.current) return;
    const canvas = qrCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const qr = qrcode(0, 'M');
    qr.addData(setupData.provisioningUri);
    qr.make();

    const size = 200;
    const moduleCount = qr.getModuleCount();
    const moduleSize = size / moduleCount;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#000000';

    for (let row = 0; row < moduleCount; row++) {
      for (let col = 0; col < moduleCount; col++) {
        if (qr.isDark(row, col)) {
          ctx.fillRect(col * moduleSize, row * moduleSize, moduleSize, moduleSize);
        }
      }
    }
  }, [setupData?.provisioningUri]);

  const handleSetup = async () => {
    setError('');
    const result = await dispatch(setup2FA());
    if (setup2FA.rejected.match(result)) {
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: result.payload as string,
      }));
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmCode || confirmCode.length !== 6) {
      setError(t('two_factor.error_code_invalid'));
      return;
    }

    const result = await dispatch(confirm2FA(confirmCode));
    if (confirm2FA.fulfilled.match(result)) {
      dispatch(updateContactData({ isTwoFactorEnabled: true }));
      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('two_factor.enabled_success'),
      }));
      setConfirmCode('');
      setError('');
    } else if (confirm2FA.rejected.match(result)) {
      setError(result.payload as string);
      setConfirmCode('');
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!disableCode || disableCode.length !== 6) {
      setError(t('two_factor.error_code_invalid'));
      return;
    }

    const result = await dispatch(disable2FA(disableCode));
    if (disable2FA.fulfilled.match(result)) {
      dispatch(updateContactData({ isTwoFactorEnabled: false }));
      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('two_factor.disabled_success'),
      }));
      setDisableCode('');
      setShowDisableForm(false);
      setError('');
    } else if (disable2FA.rejected.match(result)) {
      const payload = result.payload;
      // The backend refuses to disable 2FA while a DataSource is still
      // connected - branch by errorCode, never by status or message text.
      if (payload?.errorCode === 'two_factor_locked_by_data_source') {
        setIsDataSourceLockModalOpen(true);
        setDisableCode('');
        setError('');
      } else {
        setError(payload?.message ?? t('two_factor.error_code_invalid'));
        setDisableCode('');
      }
    }
  };

  const handleGoToDataSource = () => {
    setIsDataSourceLockModalOpen(false);
    setShowDisableForm(false);
    if (onSwitchToDataSourceTab) {
      onSwitchToDataSourceTab();
    }
  };

  const handleCancelSetup = () => {
    dispatch(clearSetupData());
    setConfirmCode('');
    setError('');
  };

  const handleCodeChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setter(cleaned);
    if (error) setError('');
  };

  if (isTwoFactorEnabled) {
    return (
      <>
        <div className="two-factor-tab">
          <div className="center">
            <h2>{t('two_factor.title')}</h2>
          </div>

          <div className="two-factor-status two-factor-status--enabled">
            <span className="two-factor-status-dot two-factor-status-dot--enabled" />
            <span>{t('two_factor.status_enabled')}</span>
          </div>

          <p className="two-factor-description">{t('two_factor.enabled_description')}</p>

          {!showDisableForm ? (
            <div className="form-actions">
              <button
                type="button"
                className="button button--danger"
                onClick={() => { setShowDisableForm(true); setError(''); }}
              >
                {t('two_factor.disable_button')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleDisable} className="two-factor-code-form">
              <p className="two-factor-description">{t('two_factor.disable_description')}</p>
              <Input
                id="disableCode"
                label={t('two_factor.code_label')}
                value={disableCode}
                onChange={handleCodeChange(setDisableCode)}
                error={error}
                placeholder="000000"
              />
              <div className="form-actions">
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => { setShowDisableForm(false); setDisableCode(''); setError(''); }}
                >
                  {t('action_cancel')}
                </button>
                <button
                  type="submit"
                  className="button button--danger"
                  disabled={disableLoading}
                >
                  {disableLoading ? t('notification_loading') : t('two_factor.disable_confirm')}
                </button>
              </div>
            </form>
          )}
        </div>

        <Modal
          isOpen={isDataSourceLockModalOpen}
          onClose={() => setIsDataSourceLockModalOpen(false)}
          title={t('two_factor.locked_by_data_source_title')}
          hideCloseButton={true}
          isCentered
        >
          <div className="center">
            <p>{t('two_factor.locked_by_data_source_message')}</p>
          </div>
          <div className="modal-buttons">
            <button
              type="button"
              className="modal-button cancel-delete"
              onClick={() => setIsDataSourceLockModalOpen(false)}
            >
              {t('action_cancel')}
            </button>
            <button
              type="button"
              className="modal-button confirm-delete"
              onClick={handleGoToDataSource}
              disabled={!onSwitchToDataSourceTab}
            >
              {t('two_factor.locked_by_data_source_button')}
            </button>
          </div>
        </Modal>
      </>
    );
  }

  if (setupData) {
    return (
      <div className="two-factor-tab">
        <div className="center">
          <h2>{t('two_factor.setup_title')}</h2>
        </div>

        <p className="two-factor-description">{t('two_factor.setup_description')}</p>

        <div className="two-factor-qr-wrapper">
          <canvas ref={qrCanvasRef} width={200} height={200} />
        </div>

        <div className="two-factor-secret">
          <label>{t('two_factor.secret_label')}</label>
          <code className="two-factor-secret-value">{setupData.secret}</code>
        </div>

        <form onSubmit={handleConfirm} className="two-factor-code-form">
          <Input
            id="confirmCode"
            label={t('two_factor.confirm_code_label')}
            value={confirmCode}
            onChange={handleCodeChange(setConfirmCode)}
            error={error}
            placeholder="000000"
          />
          <div className="form-actions">
            <button
              type="button"
              className="button button--secondary"
              onClick={handleCancelSetup}
            >
              {t('action_cancel')}
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={confirmLoading}
            >
              {confirmLoading ? t('notification_loading') : t('two_factor.confirm_button')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="two-factor-tab">
      <div className="center">
        <h2>{t('two_factor.title')}</h2>
      </div>

      <div className="two-factor-status two-factor-status--disabled">
        <span className="two-factor-status-dot two-factor-status-dot--disabled" />
        <span>{t('two_factor.status_disabled')}</span>
      </div>

      <p className="two-factor-description">{t('two_factor.disabled_description')}</p>

      <div className="form-actions">
        <button
          type="button"
          className="button button--primary"
          onClick={handleSetup}
          disabled={setupLoading}
        >
          {setupLoading ? t('notification_loading') : t('two_factor.enable_button')}
        </button>
      </div>
    </div>
  );
};

export default TwoFactorTab;
